import { useMemo } from "react"
import clsx from "clsx"
import { CardElement } from "@/types/battle"
import {
  ElementAbilityDefinition,
  getElementAbilitiesFor,
  getElementAbilityById,
  isAbilityUnlocked,
} from "@/constants/elementAbilities"
import { resolveTalentAbilityIcon } from "@/constants/abilityAssets"

export interface AbilityPickerProps {
  affiliationElement: CardElement | null | undefined
  guardianRank: number
  selectedAbilityId: string | null
  onSelect: (abilityId: string | null) => void
  disabled?: boolean
  locked?: boolean
}

export default function AbilityPicker({
  affiliationElement,
  guardianRank,
  selectedAbilityId,
  onSelect,
  disabled = false,
  locked = false,
}: AbilityPickerProps) {
  const abilities = useMemo(
    () => getElementAbilitiesFor(affiliationElement),
    [affiliationElement]
  )

  const selectedMeta = useMemo(() => {
    if (selectedAbilityId == null) {
      return { title: "Без способности", description: "Каст не выбран" }
    }
    const ability = getElementAbilityById(selectedAbilityId)
    if (!ability) {
      return { title: "Способность", description: "" }
    }
    return { title: ability.title, description: ability.description }
  }, [selectedAbilityId])

  if (!affiliationElement || abilities.length === 0) {
    return null
  }

  return (
    <div className="space-y-1.5">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-xs font-medium text-guardians-muted">Способность</span>
        {locked ? (
          <span className="text-[10px] text-guardians-muted">на весь матч</span>
        ) : null}
      </div>

      <div className="flex gap-2 overflow-x-auto pb-1">
        <AbilityChip
          title="Нет"
          selected={selectedAbilityId == null}
          locked={false}
          disabled={disabled || locked}
          onPress={() => onSelect(null)}
          empty
        />
        {abilities.map((ability: ElementAbilityDefinition) => {
          const unlocked = isAbilityUnlocked(ability, guardianRank)
          const selected = selectedAbilityId === ability.id
          const icon = resolveTalentAbilityIcon(ability.element, ability.slot)
          return (
            <AbilityChip
              key={ability.id}
              title={ability.title}
              selected={selected}
              locked={!unlocked}
              disabled={disabled || locked || !unlocked}
              onPress={() => onSelect(ability.id)}
              icon={typeof icon === "string" ? icon : undefined}
              unlockRank={ability.unlockRank}
            />
          )
        })}
      </div>

      <p className="truncate text-[10px] text-guardians-muted">
        {selectedMeta.title}
        {selectedMeta.description ? ` · ${selectedMeta.description}` : ""}
      </p>
    </div>
  )
}

function AbilityChip({
  title,
  selected,
  locked,
  disabled,
  onPress,
  empty,
  icon,
  unlockRank,
}: {
  title: string
  selected: boolean
  locked: boolean
  disabled: boolean
  onPress: () => void
  empty?: boolean
  icon?: string
  unlockRank?: number
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onPress}
      title={locked && unlockRank ? `Открывается на ранге ${unlockRank}` : title}
      className={clsx(
        "flex shrink-0 flex-col items-center gap-0.5 rounded-lg border px-2 py-1.5 text-[10px]",
        selected && "border-guardians-accent bg-guardians-accent/15",
        !selected && "border-white/15 bg-white/5",
        disabled && "opacity-45"
      )}
    >
      {empty ? (
        <span className="flex h-8 w-8 items-center justify-center rounded-md bg-black/30 text-[9px]">
          —
        </span>
      ) : icon ? (
        <img src={icon} alt="" className="h-8 w-8 rounded-md object-cover" />
      ) : (
        <span className="flex h-8 w-8 items-center justify-center rounded-md bg-black/30">
          ?
        </span>
      )}
      <span className="max-w-[4.5rem] truncate">{title}</span>
      {locked ? <span className="text-[9px] text-amber-200/80">🔒</span> : null}
    </button>
  )
}
